/**
 * Utilitaires pour formater les sélections de Coran
 */

export interface QuranSelection {
  startSurah: number;
  startVerse: number;
  endSurah: number;
  endVerse: number;
  hizb?: number;
}

interface SurahData {
  pos: number;
  ar: string;
}

/**
 * Formate une plage de versets pour l'affichage
 * @param startVerse - Verset de début
 * @param endVerse - Verset de fin (optionnel)
 * @param totalVerseCount - Nombre total de versets dans la sourate
 * @returns Chaîne formatée en arabe
 */
export function formatVerseRange(
  startVerse: number,
  endVerse: number | undefined,
  totalVerseCount: number
): string {
  // startVerse ne peut jamais être 0 (toujours >= 1)
  const startVerseValue = startVerse > 0 ? startVerse : 1;
  const endVerseValue = endVerse && endVerse > 0 ? endVerse : 0;
  const isStartVerseOne = startVerseValue === 1;
  
  // Si endVerse est spécifié
  if (endVerseValue > 0) {
    // Si endVerse n'est pas égal au nombre total de versets
    if (endVerseValue !== totalVerseCount) {
      return ` إلى الآية ${endVerseValue}`;
    }
    
    // Si endVerse est égal au nombre total de versets ou égal à 1
    if (endVerseValue === totalVerseCount || endVerseValue === 1) {
      if (isStartVerseOne) {
        return ' كاملة';
      } else {
        return ' إلى آخرها';
      }
    }
  }
  
  // Si endVerse n'est pas spécifié
  if (isStartVerseOne) {
    return ' كاملة';
  } else {
    return ' إلى آخرها';
  }
}

/**
 * Formate une sélection de Coran en format de base de données
 * Format: "surah:verse-surah:verse" (ex: "80:1-80:42")
 * 
 * @param selection - Sélection de Coran
 * @param verseCounts - Map des nombres de versets par sourate (pos -> count)
 * @param loadVerseCount - Fonction pour charger le nombre de versets d'une sourate
 * @returns Chaîne formatée ou null
 */
export async function formatPosition(
  selection: QuranSelection,
  verseCounts: Record<number, number>,
  loadVerseCount: (surah: number) => Promise<void>
): Promise<string | null> {
  if (!selection.startSurah || !selection.endSurah) return null;
  let startVerse = selection.startVerse > 0 ? selection.startVerse : 1;
  let endVerse = selection.endVerse;
  if (endVerse === 0 || endVerse < 1) {
    if (!verseCounts[selection.endSurah]) {
      await loadVerseCount(selection.endSurah);
    }
    endVerse = verseCounts[selection.endSurah] || 0;
    if (endVerse === 0) return null;
  }
  if (startVerse < 1 || endVerse < 1) return null;
  if (selection.startSurah <= selection.endSurah) {
    return `${selection.startSurah}:${startVerse}-${selection.endSurah}:${endVerse}`;
  } else {
    return `${selection.endSurah}:${endVerse}-${selection.startSurah}:${startVerse}`;
  }
}

/**
 * Formate une sélection de Coran en texte arabe pour l'affichage
 * 
 * @param selection - Sélection de Coran
 * @param type - Type de sélection ('newMemorization' | 'review' | 'assignment')
 * @param surahs - Liste des sourates avec leurs données
 * @param verseCounts - Map des nombres de versets par sourate (pos -> count)
 * @returns Texte formaté en arabe
 */
export function formatQuranSelection(
  selection: QuranSelection,
  type: 'newMemorization' | 'review' | 'assignment',
  surahs: SurahData[],
  verseCounts: Record<number, number>
): string {
  if (!selection.startSurah || !selection.endSurah) return '';
  
  const startSurahData = surahs.find(s => s.pos === selection.startSurah);
  const endSurahData = surahs.find(s => s.pos === selection.endSurah);
  
  if (!startSurahData || !endSurahData) return '';
  
  let text = 'سورة ';
  
  // Si même sourate
  if (selection.startSurah === selection.endSurah) {
    text += startSurahData.ar;
    
    const totalVerseCount = verseCounts[selection.startSurah] || 0;
    // startVerse ne peut jamais être 0 (toujours >= 1)
    const startVerse = selection.startVerse > 0 ? selection.startVerse : 1;
    const endVerse = selection.endVerse || 0;
    const isStartVerseOne = startVerse === 1;
    
    // Si endVerse est spécifié
    if (endVerse > 0) {
      // Si endVerse n'est pas égal au nombre total de versets
      if (endVerse !== totalVerseCount) {
        // Afficher "من الآية X إلى الآية Y"
        if (startVerse > 1) {
          text += ` من الآية ${startVerse} إلى الآية ${endVerse}`;
        } else {
          text += ` من الآية 1 إلى الآية ${endVerse}`;
        }
      } else {
        // Si endVerse est égal au nombre total de versets ou égal à 1
        if (endVerse === totalVerseCount || endVerse === 1) {
          if (isStartVerseOne) {
            text += ' كاملة';
          } else {
            text += ` من الآية ${startVerse} إلى آخرها`;
          }
        }
      }
    } else {
      // Si endVerse n'est pas spécifié
      if (isStartVerseOne) {
        text += ' كاملة';
      } else {
        text += ` من الآية ${startVerse} إلى آخرها`;
      }
    }
  } else {
    // Plage sur plusieurs sourates
    // L'ordre est déterminé par la relation entre startSurah et endSurah
    let order: 'asc' | 'desc';
    if (selection.endSurah > selection.startSurah) {
      order = 'asc';   // Ordre croissant (ex: 80 → 90)
    } else {
      order = 'desc';  // Ordre décroissant (ex: 90 → 80)
    }
    
    const surahsInRange: Array<{pos: number; ar: string}> = [];
    
    if (order === 'desc') {
      const startPos = Math.max(selection.startSurah, selection.endSurah);
      const endPos = Math.min(selection.startSurah, selection.endSurah);
      for (let pos = startPos; pos >= endPos; pos--) {
        const surahData = surahs.find(s => s.pos === pos);
        if (surahData) surahsInRange.push(surahData);
      }
    } else {
      const startPos = Math.min(selection.startSurah, selection.endSurah);
      const endPos = Math.max(selection.startSurah, selection.endSurah);
      for (let pos = startPos; pos <= endPos; pos++) {
        const surahData = surahs.find(s => s.pos === pos);
        if (surahData) surahsInRange.push(surahData);
      }
    }
    
    if (surahsInRange.length === 2) {
      // Pour 2 sourates, on affiche dans l'ordre d'affichage (décroissant pour "الناس", croissant pour "البقرة")
      // La première sourate dans l'ordre d'affichage
      text += surahsInRange[0].ar;
      
      // Déterminer quelle partie de cette sourate est couverte
      if (surahsInRange[0].pos === selection.endSurah) {
        // C'est la sourate de fin de la sélection
        const totalVerseCount = verseCounts[selection.endSurah] || 0;
        const endVerse = selection.endVerse || 0;
        
        if (endVerse > 0) {
          if (endVerse === totalVerseCount || endVerse === 1) {
            text += ' كاملة';
          } else {
            text += ` من الآية 1 إلى الآية ${endVerse}`;
          }
        } else {
          text += ' كاملة';
        }
      } else if (surahsInRange[0].pos === selection.startSurah) {
        // C'est la sourate de début de la sélection
        const totalVerseCount = verseCounts[selection.startSurah] || 0;
        // startVerse ne peut jamais être 0 (toujours >= 1)
        const startVerse = selection.startVerse > 0 ? selection.startVerse : 1;
        
        if (startVerse > 1) {
          text += ` من الآية ${startVerse}`;
          text += formatVerseRange(startVerse, undefined, totalVerseCount);
        } else {
          text += ' كاملة';
        }
      } else {
        // Sourate intermédiaire (entre startSurah et endSurah), elle est complète
        text += ' كاملة';
      }
      
      // La deuxième sourate dans l'ordre d'affichage
      text += ` وسورة ${surahsInRange[1].ar}`;
      
      // Déterminer quelle partie de cette sourate est couverte
      if (surahsInRange[1].pos === selection.startSurah) {
        // C'est la sourate de début de la sélection
        const totalVerseCount = verseCounts[selection.startSurah] || 0;
        // startVerse ne peut jamais être 0 (toujours >= 1)
        const startVerse = selection.startVerse > 0 ? selection.startVerse : 1;
        
        if (startVerse > 1) {
          text += ` من الآية ${startVerse}`;
          text += formatVerseRange(startVerse, undefined, totalVerseCount);
        } else {
          text += ' كاملة';
        }
      } else if (surahsInRange[1].pos === selection.endSurah) {
        // C'est la sourate de fin de la sélection
        const totalVerseCount = verseCounts[selection.endSurah] || 0;
        const endVerse = selection.endVerse || 0;
        
        if (endVerse > 0) {
          if (endVerse === totalVerseCount || endVerse === 1) {
            text += ' كاملة';
          } else {
            text += ` من الآية 1 إلى الآية ${endVerse}`;
          }
        } else {
          text += ' كاملة';
        }
      } else {
        // Sourate intermédiaire (entre startSurah et endSurah), elle est complète
        text += ' كاملة';
      }
    } else if (surahsInRange.length > 2) {
      // Plus de 2 sourates : format "من سورة ... إلى سورة ..."
      const firstSurah = surahsInRange[0];
      const lastSurah = surahsInRange[surahsInRange.length - 1];
      // Remplacer le préfixe "سورة " par "من سورة " pour éviter la répétition
      text = `من سورة ${firstSurah.ar}`;
      
      // Déterminer quelle partie de la première sourate est couverte
      if (firstSurah.pos === selection.startSurah) {
        // C'est la sourate de début
        const totalVerseCount = verseCounts[selection.startSurah] || 0;
        // startVerse ne peut jamais être 0 (toujours >= 1)
        const startVerse = selection.startVerse > 0 ? selection.startVerse : 1;
        
        if (startVerse > 1) {
          text += ` من الآية ${startVerse}`;
          text += formatVerseRange(startVerse, undefined, totalVerseCount);
        } else {
          text += ' كاملة';
        }
      } else if (firstSurah.pos === selection.endSurah) {
        // C'est la sourate de fin
        const totalVerseCount = verseCounts[selection.endSurah] || 0;
        const endVerse = selection.endVerse || 0;
        
        if (endVerse > 0) {
          text += ` من الآية ${endVerse}`;
          // startVerse ne peut jamais être 0, utiliser 1 si on commence depuis le début
          text += formatVerseRange(1, endVerse, totalVerseCount);
        } else {
          text += ' كاملة';
        }
      } else {
        // Sourate intermédiaire, elle est complète
        text += ' كاملة';
      }
      
      text += ` إلى سورة ${lastSurah.ar}`;
      
      // Déterminer quelle partie de la dernière sourate est couverte
      if (lastSurah.pos === selection.endSurah) {
        // C'est la sourate de fin
        const totalVerseCount = verseCounts[selection.endSurah] || 0;
        const endVerse = selection.endVerse || 0;
        
        if (endVerse > 0) {
          if (endVerse === totalVerseCount) {
            text += ' كاملة';
          } else {
            text += ` من الآية 1 إلى الآية ${endVerse}`;
          }
        } else {
          text += ' كاملة';
        }
      } else if (lastSurah.pos === selection.startSurah) {
        // C'est la sourate de début
        const totalVerseCount = verseCounts[selection.startSurah] || 0;
        // startVerse ne peut jamais être 0 (toujours >= 1)
        const startVerse = selection.startVerse > 0 ? selection.startVerse : 1;
        
        if (startVerse > 1) {
          text += ` من الآية ${startVerse}`;
          text += formatVerseRange(startVerse, undefined, totalVerseCount);
        } else {
          text += ' كاملة';
        }
      } else {
        // Sourate intermédiaire, elle est complète
        text += ' كاملة';
      }
    }
  }
  
  return text;
}

